import { useMemo, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router';
import { EMPTY_TODO, type LocalNote, type NoteKind } from '@/domain/note';
import { search } from '@/domain/search';
import { type Folder, findFolder, inFolder } from '@/domain/tags';
import type { ShellContext } from '@/features/shell/AppShell';
import { FabMenu } from '@/features/shell/FabMenu';
import { folderUrl } from '@/features/shell/FolderTree';
import { useDebounced, useFolders, useNotes } from '@/hooks/hooks';
import { useNoteActions } from '@/hooks/useNoteActions';
import { Chip, EmptyState } from '@/ui/components';
import type { IconName } from '@/ui/Icon';
import { NoteCard } from './NoteCard';
import { SearchBar } from './SearchBar';

export type NotesView = 'all' | 'folder' | 'shared' | 'archive';

export function filterView(notes: LocalNote[], view: NotesView, folderKey: string): LocalNote[] {
  switch (view) {
    case 'archive':
      return notes.filter((n) => n.archived);
    case 'shared':
      return notes.filter((n) => n.shared && !n.archived);
    case 'folder':
      return notes.filter((n) => !n.archived && inFolder(n.tags, folderKey));
    default:
      return notes.filter((n) => !n.archived);
  }
}

/** Initial content for a new note created inside a folder view. */
export function initialContent(kind: NoteKind, folder: Folder | undefined): string {
  const tag = folder ? `#${folder.path} ` : '';
  if (kind === 'text') return tag;
  return tag ? `${tag}\n\n${EMPTY_TODO}` : EMPTY_TODO;
}

const TITLES: Record<Exclude<NotesView, 'folder'>, string> = { all: 'Notizen', shared: 'Geteilt', archive: 'Archiv' };

function emptyState(view: NotesView, searching: boolean): { icon: IconName; title: string; text?: string } {
  if (searching) return { icon: 'search', title: 'Keine Treffer', text: 'Versuche einen anderen Suchbegriff oder #Tag.' };
  if (view === 'archive') return { icon: 'archive', title: 'Das Archiv ist leer' };
  if (view === 'shared') return { icon: 'group', title: 'Noch nichts geteilt', text: 'Geteilte Notizen erscheinen hier.' };
  return { icon: 'lightbulb', title: 'Noch keine Notizen', text: 'Tippe auf +, um eine Notiz oder Liste anzulegen.' };
}

/** Note overview: all notes, a tag folder, shared notes or the archive. */
export function NotesPage({ view }: { view: NotesView }) {
  const params = useParams();
  const folderKey = view === 'folder' ? decodeURIComponent(params.key ?? '') : '';
  const notes = useNotes();
  const { tree } = useFolders(notes);
  const folder = view === 'folder' ? findFolder(tree, folderKey) : undefined;
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 120);
  const { create, toggleTask } = useNoteActions();
  const navigate = useNavigate();
  const shell = useOutletContext<ShellContext | undefined>();

  const visible = useMemo(() => search(filterView(notes ?? [], view, folderKey), debounced), [notes, view, folderKey, debounced]);

  const onCreate = async (kind: NoteKind) => {
    const n = await create(kind, initialContent(kind, folder));
    await navigate(`/note/${n.id}`);
  };
  const openMenu = shell && !shell.isWide ? shell.openNav : undefined;
  const canCreate = view === 'all' || view === 'folder';

  return (
    <div className="page">
      <header className="page__top">
        <SearchBar value={query} onChange={setQuery} onMenu={openMenu} />
      </header>
      <Heading title={view === 'folder' ? (folder?.name ?? folderKey) : TITLES[view]} folder={folder} />
      {notes !== undefined && visible.length === 0 ? (
        <EmptyState {...emptyState(view, debounced !== '')} />
      ) : (
        <NoteSections notes={visible} onToggleTask={(id, line) => void toggleTask(id, line)} />
      )}
      {canCreate && <FabMenu onCreate={(k) => void onCreate(k)} />}
    </div>
  );
}

function Heading({ title, folder }: { title: string; folder: Folder | undefined }) {
  const navigate = useNavigate();
  const crumbs = folder ? folder.path.split(':') : [];
  return (
    <section className="page__heading">
      {crumbs.length > 1 && (
        <nav className="breadcrumbs" aria-label="Ordnerpfad">
          {crumbs.slice(0, -1).map((c, i) => (
            <Link
              key={c}
              to={folderUrl(
                crumbs
                  .slice(0, i + 1)
                  .join(':')
                  .toLocaleLowerCase('de'),
              )}
            >
              {c}
            </Link>
          ))}
        </nav>
      )}
      <h1 className="page__title">{title}</h1>
      {folder && folder.children.length > 0 && (
        <div className="chip-row" aria-label="Unterordner">
          {folder.children.map((c) => (
            <Chip key={c.key} icon="folder" label={`${c.name} · ${c.count}`} onClick={() => void navigate(folderUrl(c.key))} />
          ))}
        </div>
      )}
    </section>
  );
}

function NoteSections({ notes, onToggleTask }: { notes: LocalNote[]; onToggleTask: (id: string, line: number) => void }) {
  const pinned = notes.filter((n) => n.pinned);
  const others = notes.filter((n) => !n.pinned);
  return (
    <>
      {pinned.length > 0 && <NoteGrid label="Angeheftet" notes={pinned} onToggleTask={onToggleTask} />}
      <NoteGrid label={pinned.length > 0 ? 'Weitere' : undefined} notes={others} onToggleTask={onToggleTask} />
    </>
  );
}

function NoteGrid({
  label,
  notes,
  onToggleTask,
}: {
  label: string | undefined;
  notes: LocalNote[];
  onToggleTask: (id: string, line: number) => void;
}) {
  if (notes.length === 0) return null;
  return (
    <section className="note-section">
      {label && <h2 className="note-section__label">{label}</h2>}
      <div className="note-grid">
        {notes.map((n) => (
          <NoteCard key={n.id} note={n} onToggleTask={onToggleTask} />
        ))}
      </div>
    </section>
  );
}
